import { useEffect, useRef } from 'react';

const ARTWORK = [
  { src: '/logo192.png', sizes: '192x192', type: 'image/png' },
  { src: '/logo512.png', sizes: '512x512', type: 'image/png' },
];

/**
 * useMediaSession — integrates the Web Media Session API.
 * Populates lock-screen / notification-panel controls with Quran playback info.
 *
 * @param {object} params
 * @param {string} params.title        — verse key, e.g. "1:1"
 * @param {string} params.artist       — reciter name
 * @param {string} params.album        — surah name
 * @param {boolean} params.playing
 * @param {number}  params.duration
 * @param {number}  params.elapsed
 * @param {function} params.onPlay
 * @param {function} params.onPause
 * @param {function} params.onPrev
 * @param {function} params.onNext
 * @param {function} params.onSeek     — (details) => void
 */
const useMediaSession = ({
  title = '',
  artist = '',
  album  = '',
  playing = false,
  duration = 0,
  elapsed  = 0,
  onPlay,
  onPause,
  onPrev,
  onNext,
  onSeek,
}) => {
  const supported = typeof navigator !== 'undefined' && 'mediaSession' in navigator;
  const positionRef = useRef({ duration, elapsed });

  // Update metadata whenever verse changes
  useEffect(() => {
    if (!supported || !title) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title,
      artist,
      album,
      artwork: ARTWORK,
    });
  }, [supported, title, artist, album]);

  // Sync playback state
  useEffect(() => {
    if (!supported) return;
    navigator.mediaSession.playbackState = playing ? 'playing' : 'paused';
  }, [supported, playing]);

  // Update position state (required for seek bar in some browsers)
  useEffect(() => {
    if (!supported || !duration) return;
    positionRef.current = { duration, elapsed };
    try {
      navigator.mediaSession.setPositionState({
        duration,
        playbackRate: 1,
        position: Math.min(elapsed, duration),
      });
    } catch { /* some browsers don't support setPositionState */ }
  }, [supported, duration, elapsed]);

  // Register action handlers
  useEffect(() => {
    if (!supported) return;

    const handlers = {
      play:          onPlay  || null,
      pause:         onPause || null,
      previoustrack: onPrev  || null,
      nexttrack:     onNext  || null,
    };

    Object.entries(handlers).forEach(([action, handler]) => {
      try {
        navigator.mediaSession.setActionHandler(action, handler);
      } catch { /* not all actions supported on every platform */ }
    });

    // Seek support
    if (onSeek) {
      try {
        navigator.mediaSession.setActionHandler('seekto', (details) => {
          if (details.seekTime !== undefined) onSeek(details.seekTime / positionRef.current.duration);
        });
        navigator.mediaSession.setActionHandler('seekbackward', (details) => {
          const skipSec = details.seekOffset || 10;
          const pos = positionRef.current;
          onSeek(Math.max(0, (pos.elapsed - skipSec) / pos.duration));
        });
        navigator.mediaSession.setActionHandler('seekforward', (details) => {
          const skipSec = details.seekOffset || 10;
          const pos = positionRef.current;
          onSeek(Math.min(1, (pos.elapsed + skipSec) / pos.duration));
        });
      } catch { /* seek not supported */ }
    }

    return () => {
      ['play','pause','previoustrack','nexttrack','seekto','seekbackward','seekforward']
        .forEach((action) => {
          try { navigator.mediaSession.setActionHandler(action, null); } catch {}
        });
    };
  }, [supported, onPlay, onPause, onPrev, onNext, onSeek]);

  return { supported };
};

export default useMediaSession;
